<script setup>
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import MainLayout from "@/components/layout/MainLayout.vue";
import InvoiceCustomerCard from "@/components/invoices/InvoiceCustomerCard.vue";
import InvoiceFinancialSummaryCard from "@/components/invoices/InvoiceFinancialSummaryCard.vue";
import InvoiceInfoCard from "@/components/invoices/InvoiceInfoCard.vue";
import InvoiceLineItemsTable from "@/components/invoices/InvoiceLineItemsTable.vue";
import InvoicePaymentHistory from "@/components/invoices/InvoicePaymentHistory.vue";
import InvoiceFormModal from "@/components/invoices/InvoiceFormModal.vue";
import { useInvoiceStore } from "@/stores/invoice";
import { usePrint } from "@/composables/usePrint";

const route = useRoute();
const router = useRouter();
const invoiceStore = useInvoiceStore();
const activeTab = ref("items");

const invoiceFormModal = ref(null);

const invoiceId = computed(() => route.params.id);
const invoice = computed(() => invoiceStore.getInvoiceById(invoiceId.value));
const lineItems = computed(() =>
  invoiceStore.getLineItemsByInvoiceId(invoiceId.value),
);
const paymentItems = computed(() =>
  invoiceStore.getPaymentsByInvoiceId(invoiceId.value),
);

const goBack = () => router.push("/invoices");

const handleEdit = () => invoiceFormModal.value?.open(invoice.value);

const { printInvoice } = usePrint();
const handlePrint = () =>
  printInvoice(invoice.value, lineItems.value, paymentItems.value);
</script>

<template>
  <MainLayout>
    <div class="container-fluid px-0">
      <div v-if="invoice" class="vstack gap-4">
        <div
          class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3"
        >
          <div>
            <button
              type="button"
              class="btn btn-secondary border rounded-3 mb-3"
              @click="goBack"
            >
              <i class="bi bi-arrow-left me-1"></i>กลับไปรายการใบแจ้งหนี้
            </button>
            <div class="d-flex align-items-center gap-3 flex-wrap">
              <div class="invoice-icon">
                <i class="bi bi-receipt-cutoff"></i>
              </div>
              <div>
                <h3 class="fw-bold mb-1">{{ invoice.invoiceNo }}</h3>
                <div class="text-muted">
                  {{ invoice.billingMonth }} • {{ invoice.unitCode }} •
                  {{ invoice.residentName }}
                </div>
              </div>
            </div>
          </div>

          <div class="d-flex gap-2 flex-wrap">
            <button
              type="button"
              class="btn btn-secondary rounded-3"
              @click="handlePrint"
            >
              <i class="bi bi-printer me-1"></i>พิมพ์ใบแจ้งหนี้
            </button>
            <!-- ✅ เรียก modal แก้ไข -->
            <button
              type="button"
              class="btn btn-primary rounded-3"
              @click="handleEdit"
            >
              <i class="bi bi-pencil-square me-1"></i>แก้ไขใบแจ้งหนี้
            </button>
          </div>
        </div>

        <div class="row g-4">
          <div class="col-xl-4"><InvoiceCustomerCard :invoice="invoice" /></div>
          <div class="col-xl-4">
            <InvoiceFinancialSummaryCard :invoice="invoice" />
          </div>
          <div class="col-xl-4"><InvoiceInfoCard :invoice="invoice" /></div>
        </div>

        <div class="card border-0 shadow-sm rounded-4 overflow-hidden">
          <div class="card-header bg-white border-0 p-3">
            <ul class="nav nav-pills gap-2">
              <li class="nav-item">
                <button
                  type="button"
                  class="nav-link rounded-pill px-4"
                  :class="
                    activeTab === 'items' ? 'active' : 'text-dark bg-light'
                  "
                  @click="activeTab = 'items'"
                >
                  <i class="bi bi-list-ul me-2"></i>รายการเรียกเก็บ
                </button>
              </li>
              <li class="nav-item">
                <button
                  type="button"
                  class="nav-link rounded-pill px-4"
                  :class="
                    activeTab === 'payments' ? 'active' : 'text-dark bg-light'
                  "
                  @click="activeTab = 'payments'"
                >
                  <i class="bi bi-cash-coin me-2"></i>ประวัติการรับชำระ
                </button>
              </li>
            </ul>
          </div>
          <div class="card-body p-4 bg-light-subtle">
            <InvoiceLineItemsTable
              v-if="activeTab === 'items'"
              :items="lineItems"
            />
            <InvoicePaymentHistory v-else :items="paymentItems" />
          </div>
        </div>
      </div>

      <div v-else class="card border-0 shadow-sm rounded-4">
        <div class="card-body p-5 text-center">
          <i class="bi bi-search fs-1 text-secondary d-block mb-3"></i>
          <h4 class="fw-bold mb-2">ไม่พบข้อมูลใบแจ้งหนี้</h4>
          <p class="text-muted mb-4">
            อาจเป็นเพราะลิงก์ไม่ถูกต้อง หรือยังไม่มีข้อมูลใบแจ้งหนี้นี้ในระบบ
          </p>
          <button
            type="button"
            class="btn btn-primary rounded-3 px-4"
            @click="goBack"
          >
            <i class="bi bi-arrow-left me-2"></i>กลับไปหน้ารายการใบแจ้งหนี้
          </button>
        </div>
      </div>
    </div>

    <!-- ✅ Mount modal ไว้ที่นี่ -->
    <InvoiceFormModal ref="invoiceFormModal" />
  </MainLayout>
</template>

<style scoped>
.invoice-icon {
  width: 64px;
  height: 64px;
  border-radius: 20px;
  background: rgba(13, 110, 253, 0.12);
  color: #0d6efd;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}
</style>
